import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, MediumGroupCategoryDTO } from '../../../../services/api/api';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { UtilService } from '../../../../services/util.service';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import { finalize, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../../../services/permission.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../../services/global/RequestClass';

type SplitButtonAction = 'edit';

@Component({
  selector: 'sb-page-mediumGroup-category-list',
  templateUrl: './medium-group-category-list.component.html',
  styleUrls: ['./medium-group-category-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediumGroupCategoryListComponent extends RequestClass implements ModalNavigationComponent {
  public readonly modal = inject(ModalNavigationService);
  public readonly api = inject(API);
  public readonly route = inject(ActivatedRoute);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  showSideBar: boolean = false;
  isInModalContext: boolean;

  // table wrapper variables
  mediumGroupCategories: MediumGroupCategoryDTO[] = [];
  selectedMediumGroupCategory: MediumGroupCategoryDTO;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      return;
    }
    const sub = this.api
      .mm_MediumGroupCategory_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (categories: MediumGroupCategoryDTO[]) => {
          this.mediumGroupCategories = this.buildSplitButtons(categories);
        },
      });
    this.subs.push(sub);
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  addMediumGroupCategory() {
    this.selectedMediumGroupCategory = new MediumGroupCategoryDTO();
    this.toggleSideBar();
  }

  editMediumGroupCategory(mediumGroupCategory: MediumGroupCategoryDTO) {
    this.selectedMediumGroupCategory = mediumGroupCategory;
    this.toggleSideBar();
  }

  selectMediumGroupCategory(mediumGroupCategory: MediumGroupCategoryDTO) {
    if (this.isInModalContext) {
      this.modal.result(mediumGroupCategory);
    }
  }

  updateMediumGroupCategory(mediumGroupCategory: MediumGroupCategoryDTO) {
    if (!mediumGroupCategory.id) {
      // no piping due to creation or destroy request
      this.api.mm_MediumGroupCategory_createCategory(mediumGroupCategory).subscribe({
        next: () => {
          this.resetView();
        },
      });
    } else {
      this.api.mm_MediumGroupCategory_editCategory(mediumGroupCategory).subscribe({
        next: () => {
          this.resetView();
        },
      });
    }
  }

  resetView() {
    this.toggleSideBar();
    this.selectedMediumGroupCategory = null;
    this.requestDebounced();
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_group_category_edit])) {
        item.sbButtons.push({
          icon: 'details',
          action: 'edit',
          tooltip: 'lang.globals.details',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(mediumCategory: MediumGroupCategoryDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editMediumGroupCategory(mediumCategory);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
